import { Card } from '@zorro/design-system/v2/Card';
import { Title } from '@zorro/design-system/v2/Title';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Box, Flex, Group, Stack, Text } from '@zorro/web/design-system';
import { useMobileDetector } from '@zorro/web/ui-utils';
import { ReactElement, ReactNode } from 'react';

import { useAnalytics } from '../../Analytics';
import { PAYMENT_SECTION_WIDTH } from '../paymentsDesign.consts';
import { formatMonthlyAutoPayRecurrence } from './PaymentStatusUtils';
import { PaymentStatusCard } from './PaymentsStatusCard';

export type PaymentStatusData = {
  label: string;
  icon: ReactElement | null;
  description: string | null;
  learnMoreLink: string | null;
  toolTipLabel: string | null;
  shouldShowMarkAsDone: boolean;
};

type BaseVariantProps = {
  premiumPaymentData: PaymentStatusData;
  autoPayData: PaymentStatusData;
  monthlyAutoPayDate?: number | null;
  isMonthlyAutoPayDateEndOfMonth?: boolean;
  children?: ReactNode;
};

type ActionVariantProps = BaseVariantProps & {
  onPremiumMarkAsDone: () => void;
  onAutoPayMarkAsDone: () => void;
};

type DashboardVariantProps = ActionVariantProps & {
  shouldShowCompletedStatus?: boolean;
  shouldMakeVariantOutline?: boolean;
  onboardingPeriodId?: string;
};

export const EmployeePaymentStatusDefaultVariant = ({
  premiumPaymentData,
  autoPayData,
  monthlyAutoPayDate,
  isMonthlyAutoPayDateEndOfMonth,
  children,
}: BaseVariantProps) => {
  const autoPayRecurrence = formatMonthlyAutoPayRecurrence(
    monthlyAutoPayDate,
    isMonthlyAutoPayDateEndOfMonth
  );

  return (
    <Card variant="white-border" pos="relative">
      <Group gap="xl" grow justify="space-between">
        <Stack>
          <Title order={3} size="sm">
            Initial premium payment
          </Title>
          <Group gap="xs">
            {premiumPaymentData.icon}
            <Text size="sm">{premiumPaymentData.label}</Text>
          </Group>
        </Stack>

        <Stack>
          <Title order={3} size="sm">
            Auto-Pay
          </Title>
          <Tooltip
            position="top"
            disabled={!autoPayRecurrence}
            label={
              <Stack gap="var(--space-4)">
                <Text size="sm" fw={600}>
                  Monthly auto-pay
                </Text>
                <Text size="sm">Recurring on {autoPayRecurrence}</Text>
              </Stack>
            }
          >
            <Group gap="var(--space-8)" w="fit-content">
              {autoPayData.icon}
              <Text size="sm">{autoPayData.label}</Text>
            </Group>
          </Tooltip>
        </Stack>
      </Group>
      {children}
    </Card>
  );
};

export const EmployeePaymentStatusElenaVariant = ({
  premiumPaymentData,
  autoPayData,
  onPremiumMarkAsDone,
  onAutoPayMarkAsDone,
  children,
}: ActionVariantProps) => {
  return (
    <Card
      maw={PAYMENT_SECTION_WIDTH}
      p={32}
      variant="white-border"
      pos="relative"
    >
      <Flex direction={{ base: 'column', md: 'row' }} gap="xl">
        <PaymentStatusCard
          {...premiumPaymentData}
          onMarkAsDone={onPremiumMarkAsDone}
        />
        <PaymentStatusCard
          {...autoPayData}
          onMarkAsDone={onAutoPayMarkAsDone}
        />
      </Flex>
      {children}
    </Card>
  );
};

export const EmployeePaymentStatusDashboardVariant = ({
  premiumPaymentData,
  autoPayData,
  onPremiumMarkAsDone,
  onAutoPayMarkAsDone,
  shouldShowCompletedStatus,
  children,
  shouldMakeVariantOutline,
  onboardingPeriodId,
}: DashboardVariantProps) => {
  const { sendEvent } = useAnalytics();
  const { isMobile } = useMobileDetector();
  const shouldShowPremium =
    shouldShowCompletedStatus || premiumPaymentData.shouldShowMarkAsDone;
  const shouldShowAutoPay =
    shouldShowCompletedStatus || autoPayData.shouldShowMarkAsDone;

  const handleLearnMoreClick = async () => {
    await sendEvent('click_learn_more', {
      onboarding_period_id: onboardingPeriodId,
    });
  };

  return (
    <>
      <Flex direction={{ base: 'column', md: 'row' }} gap="xl" h="100%">
        {shouldShowPremium && (
          <Box w={isMobile ? '100%' : '50%'}>
            <PaymentStatusCard
              {...premiumPaymentData}
              onMarkAsDone={onPremiumMarkAsDone}
              handleLearnMoreClick={handleLearnMoreClick}
              shouldMakeCardOutline={shouldMakeVariantOutline}
              isDashboard
            />
          </Box>
        )}
        {shouldShowAutoPay && (
          <Box w={isMobile ? '100%' : '50%'}>
            <PaymentStatusCard
              {...autoPayData}
              onMarkAsDone={onAutoPayMarkAsDone}
              handleLearnMoreClick={handleLearnMoreClick}
              shouldMakeCardOutline={shouldMakeVariantOutline}
              isDashboard
            />
          </Box>
        )}
      </Flex>
      {children}
    </>
  );
};
